/**
 * textDiff - a small, dependency-free diff for version comparison (redline).
 *
 * Version content arrives as markdown/plain text. We diff at the LINE level with
 * a classic LCS (few hundred lines in a document, so O(n·m) is fine), then do a
 * WORD-level pass inside a changed block so a one-word edit shows as a one-word
 * redline rather than a whole paragraph flip. No external dependency - a diff
 * library isn't worth adding for this.
 */

export type DiffOp = 'equal' | 'add' | 'del';

export interface DiffLine {
  op: DiffOp;
  /** For 'equal'/'add'/'del' with no intra-line detail, the whole line text. */
  text?: string;
  /** For a changed line rendered as a word-level redline, the segments. */
  segments?: { op: DiffOp; text: string }[];
}

/** LCS table over two arrays of strings; returns the aligned op sequence. */
function lcsDiff<T>(a: T[], b: T[]): { op: DiffOp; a?: T; b?: T }[] {
  const n = a.length;
  const m = b.length;
  // dp[i][j] = LCS length of a[i:] and b[j:]
  const dp: number[][] = Array.from({ length: n + 1 }, () => new Array(m + 1).fill(0));
  for (let i = n - 1; i >= 0; i--) {
    for (let j = m - 1; j >= 0; j--) {
      dp[i][j] = a[i] === b[j] ? dp[i + 1][j + 1] + 1 : Math.max(dp[i + 1][j], dp[i][j + 1]);
    }
  }
  const out: { op: DiffOp; a?: T; b?: T }[] = [];
  let i = 0;
  let j = 0;
  while (i < n && j < m) {
    if (a[i] === b[j]) {
      out.push({ op: 'equal', a: a[i], b: b[j] });
      i++; j++;
    } else if (dp[i + 1][j] >= dp[i][j + 1]) {
      out.push({ op: 'del', a: a[i] });
      i++;
    } else {
      out.push({ op: 'add', b: b[j] });
      j++;
    }
  }
  while (i < n) { out.push({ op: 'del', a: a[i] }); i++; }
  while (j < m) { out.push({ op: 'add', b: b[j] }); j++; }
  return out;
}

/** Split into words while keeping the whitespace as its own tokens. */
function tokenize(line: string): string[] {
  return line.match(/\s+|[^\s]+/g) || [];
}

/** A word-level redline for a single changed line (old → new). */
function wordSegments(oldLine: string, newLine: string): { op: DiffOp; text: string }[] {
  const ops = lcsDiff(tokenize(oldLine), tokenize(newLine));
  const segs: { op: DiffOp; text: string }[] = [];
  for (const o of ops) {
    const text = (o.op === 'add' ? o.b : o.a) as string;
    const last = segs[segs.length - 1];
    if (last && last.op === o.op) last.text += text;
    else segs.push({ op: o.op, text });
  }
  return segs;
}

/**
 * Line-level redline between two texts. Consecutive del+add pairs are collapsed
 * into a single word-level redline line so small edits read as small changes.
 */
export function diffText(oldText: string, newText: string): DiffLine[] {
  const oldLines = (oldText || '').replace(/\r\n/g, '\n').split('\n');
  const newLines = (newText || '').replace(/\r\n/g, '\n').split('\n');
  const ops = lcsDiff(oldLines, newLines);

  const lines: DiffLine[] = [];
  for (let k = 0; k < ops.length; k++) {
    const o = ops[k];
    if (o.op === 'equal') {
      lines.push({ op: 'equal', text: o.a as string });
      continue;
    }
    // Pair a del immediately followed by an add → word-level redline for that line.
    if (o.op === 'del' && ops[k + 1] && ops[k + 1].op === 'add') {
      lines.push({ op: 'equal', segments: wordSegments(o.a as string, ops[k + 1].b as string) });
      k++; // consume the paired add
      continue;
    }
    lines.push({ op: o.op, text: (o.op === 'del' ? o.a : o.b) as string });
  }
  return lines;
}

/** True when the two texts are identical after newline normalisation. */
export function textsIdentical(a: string, b: string): boolean {
  return (a || '').replace(/\r\n/g, '\n') === (b || '').replace(/\r\n/g, '\n');
}
